import axios from "axios";

const API_URL = "/api";

export const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_BASE}`,
  withCredentials: true,
});

export const djangoAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_ADMIN}`,
  withCredentials: true,
});

export const login = async (username, password) => {
  const response = await djangoAxiosInstance.post("/users/auth/login/", {
    email: username,
    password,
  });
  return response.data;
};

export const checkActivationToken = async (token) => {
  const response = djangoAxiosInstance.get(`/users/activate/${token}/`);

  return response;
};

export const activateUser = async (token, activationData) => {
  return djangoAxiosInstance.post(`/users/activate/${token}/`, activationData);
};

export const registerUser = async (values) => {
  const response = djangoAxiosInstance.post("/users/auth/register/", values);

  return response;
};

export const documentRequest = async (
  userInput,
  requiredRoles = ["user", "admin", "superuser"],
) => {
  const response = await axiosInstance.post("/api/document_request", {
    user_input: userInput,
    required_roles: requiredRoles,
  });
  return response.data;
};

export const generateDocuments = async (
  data,
  requiredRoles = ["user", "admin", "superuser"],
) => {
  const response = await axiosInstance.post("/api/generate_documents", {
    data: data,
    required_roles: requiredRoles,
  });
  return response.data;
};

export const checkAuth = async () => {
  try {
    const response = await axiosInstance.get("/api/auth/verify_token");
    return {
      isValid: response.status === 200,
      username: response.data.username,
      tenantName: response.data.tenant_name,
    };
  } catch (error) {
    console.error("Authentication check failed:", error);
    return {
      isValid: false,
      username: null,
      tenantName: null,
    };
  }
};

export const logout = async () => {
  await axiosInstance.post("/api/auth/logout");
  localStorage.removeItem("access_token");
};

export const createTemplate = async (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const response = await axiosInstance.post("/api/create_template", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const addField = async (chunkId, fieldName) => {
  const response = await axiosInstance.post("/api/add_field", {
    chunk_id: chunkId,
    field_name: fieldName,
  });
  return response.data;
};

export const createPreset = async (chunkId, presetName) => {
  const response = await axiosInstance.post("/api/create_preset", {
    chunk_id: chunkId,
    preset_name: presetName,
  });
  return response.data;
};

export const reclassifyChunk = async (chunkId, newClassification) => {
  const response = await axiosInstance.post("/api/reclassify_chunk", {
    chunk_id: chunkId,
    new_classification: newClassification,
  });
  return response.data;
};

export const chunkDocument = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await fetch("/api/chunk_document", {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const { value } = await reader.read();
    const text = new TextDecoder().decode(value);
    return JSON.parse(text);
  } catch (error) {
    console.error("Error in chunkDocument:", error);
    throw error;
  }
};

export const getChunkDocumentStatus = async (jobId) => {
  const response = await axiosInstance.get(`/api/chunk_document_status/${jobId}`);
  return response.data;
};

export const getJobStatus = async (jobId) => {
  const response = await axiosInstance.get(`/api/job_status/${jobId}`);
  return response.data;
};

export const joinEarlyAccess = async (contactData) => {
  const response = await axiosInstance.post(
    "/api/join_early_access",
    contactData,
  );
  return response.data;
};

export const unsubscribe = async (email) => {
  const response = await axiosInstance.post("/api/unsubscribe", { email });
  return response.data;
};

export const verifySignupToken = async (token) => {
  const response = await axiosInstance.post("/api/auth/verify-signup-token", {
    token,
  });
  return response.data;
};

export const createAccount = async (token, password, acceptedTerms) => {
  const response = await axiosInstance.post("/api/auth/create-account", {
    token,
    password,
    acceptedTerms,
    role: "user",
  });
  return response.data;
};

export const deleteAccount = async () => {
  const response = await axiosInstance.post("/api/auth/delete-account");
  localStorage.removeItem("access_token");
  return response.data;
};

export const submitFeedback = async (feedbackText) => {
  const response = await axiosInstance.post("/api/feedback", {
    feedback_text: feedbackText,
  });
  return response.data;
};

/*******************************************************
 * Password reset routes
 *******************************************************/

export const requestPasswordReset = async (email) => {
  const response = await axiosInstance.post(
    "/api/auth/forgot-password",
    email,
    {
      headers: {
        "Content-Type": "text/plain",
      },
    },
  );
  return response.data;
};

export const resetPassword = async (token, password) => {
  const response = await axiosInstance.post("/api/auth/reset-password", {
    token,
    password,
  });
  return response.data;
};

export const validateResetToken = async (token) => {
  const response = await axiosInstance.post(
    "/api/auth/validate-reset-token",
    token,
    {
      headers: {
        "Content-Type": "text/plain",
      },
    },
  );
  return response.data;
};
